import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import type { FeatureComparisonBlock } from "../../../types/content";
import { sanitizeHtml } from "@/utils/sanitizeHtml";
import styles from "./FeatureComparison.module.css";

export interface FeatureComparisonProps {
  block: FeatureComparisonBlock;
  className?: string;
}

const MOBILE_QUERY = "(max-width: 768px)"; // mirrors --breakpoint-md
const TOOLTIP_GAP = 8;
const TOOLTIP_MARGIN = 8;
const TOOLTIP_ARROW_INSET = 12;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setIsMobile(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

const CheckIcon: React.FC = () => (
  <svg
    className={styles.checkIcon}
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M3 9.5L7 13.5L15 4.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CrossIcon: React.FC = () => (
  <svg
    className={styles.crossIcon}
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M4 4L14 14M14 4L4 14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InfoIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <circle
      cx="8"
      cy="8"
      r="7"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
    <path
      d="M8 7V11.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="8" cy="4.75" r="0.9" fill="currentColor" />
  </svg>
);

const SWIPE_THRESHOLD = 50;

export const FeatureComparison: React.FC<FeatureComparisonProps> = ({
  block,
  className,
}) => {
  const [activePlanIndex, setActivePlanIndex] = useState(0);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const isMobile = useIsMobile();
  const [openTooltipId, setOpenTooltipId] = useState<string | null>(null);
  const tooltipTriggerRef = useRef<HTMLButtonElement | null>(null);
  const tooltipPopupRef = useRef<HTMLDivElement | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{
    top: number;
    left: number;
    arrowLeft: number;
  } | null>(null);

  const { heading, subtitle, planNames, features, footerText, ctaButton } =
    block;

  // Position the mobile tooltip below the trigger, centred on the icon, then
  // clamped to the viewport so it never renders off-screen (GROW-311).
  useLayoutEffect(() => {
    if (!openTooltipId) {
      setTooltipPos(null);
      return;
    }
    const trigger = tooltipTriggerRef.current;
    const popup = tooltipPopupRef.current;
    if (!trigger || !popup) return;
    const rect = trigger.getBoundingClientRect();
    const popupWidth = popup.offsetWidth;
    const iconCenterX = rect.left + rect.width / 2;
    const maxLeft = Math.max(
      TOOLTIP_MARGIN,
      window.innerWidth - popupWidth - TOOLTIP_MARGIN
    );
    const left = Math.min(
      Math.max(iconCenterX - popupWidth / 2, TOOLTIP_MARGIN),
      maxLeft
    );
    const arrowLeft = Math.min(
      Math.max(iconCenterX - left, TOOLTIP_ARROW_INSET),
      popupWidth - TOOLTIP_ARROW_INSET
    );
    setTooltipPos({ top: rect.bottom + TOOLTIP_GAP, left, arrowLeft });
  }, [openTooltipId]);

  // Dismiss the open tooltip on outside click, Escape, scroll or resize.
  useEffect(() => {
    if (!openTooltipId) return;
    const close = () => setOpenTooltipId(null);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        tooltipPopupRef.current?.contains(target) ||
        tooltipTriggerRef.current?.contains(target)
      ) {
        return;
      }
      close();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("scroll", close, { capture: true, passive: true });
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("scroll", close, { capture: true });
      window.removeEventListener("resize", close);
    };
  }, [openTooltipId]);

  // Close any open tooltip when leaving the mobile breakpoint.
  useEffect(() => {
    if (!isMobile) setOpenTooltipId(null);
  }, [isMobile]);

  if (!features || features.length === 0) return null;
  if (!planNames || planNames.length === 0) return null;

  const planCount = planNames.length;
  const gridStyle = { "--plan-count": planCount } as React.CSSProperties;
  const activeIndex = Math.min(activePlanIndex, planCount - 1);

  const containerClasses = [styles.featureComparison, className]
    .filter(Boolean)
    .join(" ");

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart.current) return;
    const touch = e.changedTouches[0];
    if (!touch) return;
    const dx = touchStart.current.x - touch.clientX;
    const dy = touchStart.current.y - touch.clientY;
    touchStart.current = null;
    // Ignore if the swipe is more vertical than horizontal
    if (Math.abs(dy) > Math.abs(dx)) return;
    if (Math.abs(dx) > SWIPE_THRESHOLD) {
      if (dx > 0) {
        setActivePlanIndex((prev) => Math.min(prev + 1, planCount - 1));
      } else {
        setActivePlanIndex((prev) => Math.max(prev - 1, 0));
      }
    }
  };

  return (
    <section className={containerClasses} data-component="feature-comparison">
      <div className={styles.inner}>
        {/* Header + Table */}
        <div className={styles.header}>
          {(heading || subtitle) && (
            <div className={styles.titles}>
              {heading && <h2 className={styles.heading}>{heading}</h2>}
              {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
            </div>
          )}

          <div className={styles.table}>
            {/* Plan column headers */}
            <div className={styles.planHeaders}>
              <div className={styles.planHeaderGroup} style={gridStyle}>
                {planNames.map((name, i) => (
                  <button
                    key={i}
                    className={[
                      styles.planName,
                      i === activeIndex ? styles.planNameActive : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => setActivePlanIndex(i)}
                    aria-pressed={i === activeIndex}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>

            {/* Feature rows */}
            <div
              className={styles.rows}
              data-active-plan={activeIndex}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {features.map((feature) => {
                const safePlanAvailability = Array.isArray(
                  feature.planAvailability
                )
                  ? feature.planAvailability
                  : [];
                const values = Array.from(
                  { length: planCount },
                  (_, i) => safePlanAvailability[i] ?? false
                );
                return (
                  <React.Fragment key={feature.id}>
                    {feature.groupHeading && (
                      <h3 className={styles.groupHeader}>
                        {feature.groupHeading}
                      </h3>
                    )}
                    <div className={styles.featureRow}>
                      <span className={styles.featureName}>
                        {feature.featureName}
                        {feature.tooltip && (
                          <button
                            type="button"
                            className={styles.tooltipTrigger}
                            aria-label={feature.tooltip}
                            data-tooltip={feature.tooltip}
                            aria-describedby={
                              openTooltipId === feature.id
                                ? `tt-${feature.id}`
                                : undefined
                            }
                            aria-expanded={
                              isMobile
                                ? openTooltipId === feature.id
                                : undefined
                            }
                            onClick={
                              isMobile
                                ? (e) => {
                                    tooltipTriggerRef.current = e.currentTarget;
                                    setOpenTooltipId((cur) =>
                                      cur === feature.id ? null : feature.id
                                    );
                                  }
                                : undefined
                            }
                          >
                            <InfoIcon />
                          </button>
                        )}
                      </span>
                      <div className={styles.planValues} style={gridStyle}>
                        {values.map((included, i) => (
                          <span
                            key={i}
                            role="img"
                            aria-label={
                              included
                                ? `${planNames[i]}: included`
                                : `${planNames[i]}: not included`
                            }
                          >
                            {included ? <CheckIcon /> : <CrossIcon />}
                          </span>
                        ))}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        {(footerText || ctaButton) && (
          <div className={styles.footer}>
            {footerText && (
              <div
                className={styles.footerContent}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(footerText) }}
              />
            )}
            {ctaButton && (
              <a
                href={ctaButton.url}
                className={styles.ctaButton}
                target={ctaButton.target || undefined}
              >
                {ctaButton.name}
              </a>
            )}
          </div>
        )}
      </div>

      {isMobile &&
        openTooltipId &&
        createPortal(
          <div
            ref={tooltipPopupRef}
            id={`tt-${openTooltipId}`}
            role="tooltip"
            className={styles.mobileTooltip}
            style={{
              top: tooltipPos?.top ?? -9999,
              left: tooltipPos?.left ?? 0,
              visibility: tooltipPos ? "visible" : "hidden",
            }}
          >
            {features.find((f) => f.id === openTooltipId)?.tooltip}
            <span
              className={styles.mobileTooltipArrow}
              style={{ left: tooltipPos?.arrowLeft ?? 0 }}
            />
          </div>,
          document.body
        )}
    </section>
  );
};

export default FeatureComparison;
